import React, { useEffect, useState, useCallback } from 'react';
import { Building2, Plus, Search, Edit2, Trash2, Upload, CheckCircle2, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { LEAD_SOURCES } from './EnquirySalesFields';

const API_URL = window.location.origin;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const inr = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const STAGES = [
  { value: 'prospect', label: 'Prospect', color: 'bg-blue-100 text-blue-700' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'inactive', label: 'Inactive', color: 'bg-slate-100 text-slate-600' },
];
const SOURCES = LEAD_SOURCES;
const EMPTY = { name: '', contact_person: '', phone: '', email: '', address: '', city: '', gst_number: '', industry: '', source: '', stage: 'prospect', assigned_to: '', notes: '' };
const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm';

const Field = ({ label, children }) => (
  <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}</div>
);

const CustomerModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/sales-crm/customers${initial?.id ? `/${initial.id}` : ''}`, {
        method: initial?.id ? 'PUT' : 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      toast.success(initial?.id ? 'Customer updated' : 'Customer added');
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <form onSubmit={save} className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sales-customer-modal">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold">{initial?.id ? 'Edit Sales Customer' : 'New Sales Customer'}</h2>
          <button type="button" onClick={onClose} data-testid="sales-customer-modal-close"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Field label="Company Name *"><input required value={form.name} onChange={set('name')} className={inputCls} data-testid="sc-name-input" /></Field></div>
          <Field label="Contact Person"><input value={form.contact_person || ''} onChange={set('contact_person')} className={inputCls} data-testid="sc-contact-input" /></Field>
          <Field label="Phone"><input value={form.phone || ''} onChange={set('phone')} className={inputCls} data-testid="sc-phone-input" /></Field>
          <Field label="Email"><input type="email" value={form.email || ''} onChange={set('email')} className={inputCls} data-testid="sc-email-input" /></Field>
          <Field label="City"><input value={form.city || ''} onChange={set('city')} className={inputCls} data-testid="sc-city-input" /></Field>
          <div className="sm:col-span-2"><Field label="Address"><input value={form.address || ''} onChange={set('address')} className={inputCls} data-testid="sc-address-input" /></Field></div>
          <Field label="GST Number"><input value={form.gst_number || ''} onChange={set('gst_number')} className={inputCls} data-testid="sc-gst-input" /></Field>
          <Field label="Industry"><input value={form.industry || ''} onChange={set('industry')} className={inputCls} placeholder="e.g. Pharma, Data Centre" data-testid="sc-industry-input" /></Field>
          <Field label="Lead Source">
            <select value={form.source || ''} onChange={set('source')} className={inputCls} data-testid="sc-source-select">
              <option value="">Select source</option>{SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Stage">
            <select value={form.stage || 'prospect'} onChange={set('stage')} className={inputCls} data-testid="sc-stage-select">
              {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Account Owner"><input value={form.assigned_to || ''} onChange={set('assigned_to')} className={inputCls} placeholder="Salesperson name" data-testid="sc-owner-input" /></Field>
          <div className="sm:col-span-2"><Field label="Notes"><textarea rows={2} value={form.notes || ''} onChange={set('notes')} className={inputCls} data-testid="sc-notes-input" /></Field></div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm bg-slate-100 rounded-lg hover:bg-slate-200">Cancel</button>
          <button type="submit" disabled={saving} data-testid="sc-save-btn" className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
};

const CustomerRow = ({ c, onEdit, onDelete, onPush }) => {
  const stage = STAGES.find(s => s.value === c.stage) || STAGES[0];
  return (
    <tr className="hover:bg-slate-50" data-testid={`sc-row-${c.id}`}>
      <td className="px-4 py-3">
        <p className="font-medium text-slate-900">{c.name}</p>
        <p className="text-xs text-slate-500">{[c.industry, c.city].filter(Boolean).join(' • ') || '-'}</p>
      </td>
      <td className="px-4 py-3 text-sm">
        <p className="text-slate-700">{c.contact_person || '-'}</p>
        <p className="text-xs text-slate-500">{c.phone || c.email || ''}</p>
      </td>
      <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-medium rounded-full ${stage.color}`}>{stage.label}</span></td>
      <td className="px-4 py-3 text-sm text-slate-600">{c.source || '-'}</td>
      <td className="px-4 py-3 text-sm text-right tabular-nums">{c.stats?.enquiries || 0}</td>
      <td className="px-4 py-3 text-sm text-right tabular-nums">{inr(c.stats?.quoted_value)}</td>
      <td className="px-4 py-3 text-sm text-right tabular-nums text-green-700">{inr(c.stats?.won_value)}</td>
      <td className="px-4 py-3">
        {c.master_client_id ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`sc-in-master-${c.id}`}><CheckCircle2 className="w-4 h-4" /> In master</span>
        ) : (
          <button onClick={() => onPush(c)} data-testid={`sc-push-btn-${c.id}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-amber-800 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors">
            <Upload className="w-3.5 h-3.5" /> Push to master
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-center gap-1">
          <button onClick={() => onEdit(c)} title="Edit" data-testid={`sc-edit-btn-${c.id}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4" /></button>
          <button onClick={() => onDelete(c)} title="Delete" data-testid={`sc-delete-btn-${c.id}`} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
        </div>
      </td>
    </tr>
  );
};

export default function SalesCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set('search', search);
      if (stage) qs.set('stage', stage);
      const res = await fetch(`${API_URL}/api/sales-crm/customers?${qs}`, { headers: auth() });
      if (!res.ok) throw new Error();
      setCustomers((await res.json()).customers || []);
    } catch {
      toast.error('Failed to load sales customers');
    } finally {
      setLoading(false);
    }
  }, [search, stage]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const remove = async (c) => {
    if (!window.confirm(`Delete sales customer "${c.name}"? The master client list is not affected.`)) return;
    const res = await fetch(`${API_URL}/api/sales-crm/customers/${c.id}`, { method: 'DELETE', headers: auth() });
    if (res.ok) { toast.success('Customer deleted'); load(); } else toast.error('Failed to delete');
  };

  const push = async (c) => {
    if (!window.confirm(`Add "${c.name}" to the master client list? If a master client with the same name exists, it will be linked instead.`)) return;
    const res = await fetch(`${API_URL}/api/sales-crm/customers/${c.id}/push-to-master`, { method: 'POST', headers: auth() });
    const data = await res.json();
    if (res.ok) { toast.success(data.message); load(); } else toast.error(data.detail || 'Failed to push');
  };

  const counts = STAGES.map(s => ({ ...s, n: customers.filter(c => c.stage === s.value).length }));

  return (
    <div className="space-y-6 max-w-[calc(100vw-3rem)] md:max-w-none" data-testid="sales-customers-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Building2 className="w-6 h-6 text-emerald-600" /> Sales Customers</h1>
          <p className="text-slate-500 mt-1 text-sm">Prospects and accounts the sales team works with — kept separate from the master client list</p>
        </div>
        <div className="flex gap-2">
          <Link to="/sales/customer-management" data-testid="sc-insights-link" className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50">Master customer insights</Link>
          <button onClick={() => setEditing({})} data-testid="sc-add-btn" className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800">
            <Plus className="w-4 h-4" /> New Customer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="sc-count-total"><p className="text-xs text-slate-500">Total</p><p className="text-2xl font-bold">{customers.length}</p></div>
        {counts.map(s => (
          <div key={s.value} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`sc-count-${s.value}`}><p className="text-xs text-slate-500">{s.label}</p><p className="text-2xl font-bold">{s.n}</p></div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, contact, city, email..." data-testid="sc-search-input"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm" />
        </div>
        <select value={stage} onChange={e => setStage(e.target.value)} data-testid="sc-stage-filter" className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white">
          <option value="">All stages</option>{STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button onClick={load} data-testid="sc-refresh-btn" className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {['Customer', 'Contact', 'Stage', 'Source'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">{h}</th>)}
              {['Enquiries', 'Quoted', 'Won'].map(h => <th key={h} className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">{h}</th>)}
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Master List</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!loading && customers.length === 0 ? (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500" data-testid="sc-empty">
                No sales customers yet. <button onClick={() => setEditing({})} className="text-blue-600 hover:underline">Add the first one</button>
              </td></tr>
            ) : customers.map(c => <CustomerRow key={c.id} c={c} onEdit={setEditing} onDelete={remove} onPush={push} />)}
          </tbody>
        </table>
      </div>

      {editing && <CustomerModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
